import { Container, OrderHeader, Section, TableHeader } from "./CustomerOrderTable.styled"
import CustomerOrderTableRow from "./CustomerOrderTableRow"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"
import { useSelector } from "react-redux"
import { customerSelect } from "lib/store/services/customer/customerSelector"
import { selectSidebarCollapsed } from "lib/store/services/ui/selectors"
import { useLazyQuery } from "@apollo/client"
import SEARCH_ORDERS from "../../graphql/queries/order/SearchOrders"
import React, { Dispatch, KeyboardEvent, SetStateAction, useEffect } from "react"
import ErrorMessage from "../Ui/Messages/ErrorMessage"
import { ClearButton, InputWrapper, SearchButton } from "../../views/Customer/Customer.styled"
import { ReactComponent as SearchIcon } from "images/icons/magnifying-glass.svg"
import { ReactComponent as XmarkIcon } from "images/icons/xmark.svg"
import { NoticeBanner } from "../Ui/Messages/NoticeBanner"
import { useNavigate } from "react-router-dom"

type Props = {
  customerOrders: CustomerOrders
  searchString: string
  setSearchString: Dispatch<SetStateAction<string>>
  email: string
}

const CustomerOrderTable = ({ customerOrders, searchString, setSearchString, email }: Props) => {
  const customer = useSelector(customerSelect)
  const sideBarCollapsed = useSelector(selectSidebarCollapsed)
  const navigate = useNavigate()

  const clearCustomer = () => {
    customerOrders.clearAll()
    setSearchString("")
    if (email) navigate(location.pathname, { replace: true, state: null })
  }

  const [searchOrders, { error, loading }] = useLazyQuery(SEARCH_ORDERS, {
    onCompleted: (data) => {
      customerOrders.setEmail(searchString)
      customerOrders.setOrders(data.searchOrders.hits)
    }
  })

  const searchCustomerOrders = () => {
    searchOrders(customerOrders.getSearchVariables(searchString))
  }

  useEffect(() => {
    if (email || customer.email) {
      searchOrders(customerOrders.getSearchVariables(searchString))
    }
  }, [email])

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      searchCustomerOrders()
    }
  }

  return (
    <Container className={loading ? "blur" : ""}>
      <InputWrapper>
        <input
          onChange={(e) => setSearchString(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={"Search email address"}
          value={searchString}
        />
        {searchString !== "" && (
          <ClearButton onClick={clearCustomer}>
            <XmarkIcon />
          </ClearButton>
        )}
        <SearchButton onClick={searchCustomerOrders} disabled={searchString === ""}>
          <SearchIcon />
        </SearchButton>
      </InputWrapper>
      {error && <ErrorMessage message={"Error when fetching orders"} />}
      {customer.email && (
        <>
          <NoticeBanner type="info">These are orders placed by {customer.email}.</NoticeBanner>
          {customer?.orders && customer?.orders?.length > 0 ? (
            <TableHeader $sideBarCollapsed={sideBarCollapsed}>
              <OrderHeader>Order</OrderHeader>
              <Section className={"hideOnMediumScreens"}>Store group</Section>
              <Section className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
                Store market
              </Section>
              <Section>Date & time</Section>
              <Section>Operations</Section>
            </TableHeader>
          ) : (
            <div>- No orders found</div>
          )}
        </>
      )}
      {customer?.orders?.map((order) => (
        <CustomerOrderTableRow
          searchOrder={order}
          key={order.id}
          customerOrders={customerOrders}
          sideBarCollapsed={sideBarCollapsed}
        />
      ))}
    </Container>
  )
}
export default CustomerOrderTable
